import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Bell, BarChart3, FileText, Image, Package, CreditCard, Pill, Store,
  BookOpen, Landmark, Wallet, Banknote, CircleCheck, Gift, Megaphone,
  Handshake, Users, UserCog, Medal, Settings, LogOut, Menu, X,
  PanelLeftClose, PanelLeftOpen, Activity
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import useAdminPermissions from '../hooks/useAdminPermissions';
import useResponsive from '../hooks/useResponsive';
import ScrollStyle from './ScrollStyle';
import { C, RADIUS, SHADOW } from '../styles/theme';
import logo from '../assets/startup/static-logo.png';

// Desktop sidebar widths. Layout.js pads admin pages by whichever is current,
// so the two must stay in step.
export const ADMIN_SIDEBAR_WIDTH = 304;
export const ADMIN_SIDEBAR_COLLAPSED = 68;

// Navigation grouped by what the admin is actually trying to do, rather than
// one flat list with everything money-shaped swept into a drawer at the bottom.
//
// `perm` mirrors the PermissionGate on the matching route in App.js. Those two
// must agree: a link that leads to an Access Denied screen is worse than no
// link, because it reads as a broken page rather than a closed door.
//   null           -> always visible
//   'super_admin'  -> super admin only
//   anything else  -> that permission column on admin_permissions
const SECTIONS = [
  {
    title: 'Overview',
    tint: C.indigo,
    tintLight: C.indigoLight,
    caption: 'Where things stand today',
    items: [
      { label: 'Home', Icon: Home, path: '/admin', perm: null },
      { label: 'Alerts', Icon: Bell, path: '/admin/notifications', perm: null },
      { label: 'Reports', Icon: BarChart3, path: '/admin/reports', perm: 'can_view_reports' },
    ],
  },
  {
    title: 'Order flow',
    tint: C.blue,
    tintLight: C.blueLight,
    caption: 'Quote request through to paid order',
    items: [
      { label: 'Quotes', Icon: FileText, path: '/admin/quotes', perm: 'can_process_quotes' },
      { label: 'Image Orders', Icon: Image, path: '/admin/image-orders', perm: 'can_process_quotes' },
      { label: 'Orders', Icon: Package, path: '/admin/orders', perm: 'can_update_delivery' },
      { label: 'Payments', Icon: CreditCard, path: '/admin/payments', perm: 'can_verify_payments' },
    ],
  },
  {
    title: 'Catalogue',
    tint: C.purple,
    tintLight: C.purpleLight,
    caption: 'What is for sale, and who supplies it',
    items: [
      { label: 'Products', Icon: Pill, path: '/admin/products', perm: 'can_manage_products' },
      { label: 'Vendors', Icon: Store, path: '/admin/vendors', perm: 'can_manage_users' },
    ],
  },
  {
    title: 'Finance',
    tint: C.primary,
    tintLight: C.primaryLight,
    caption: 'Ledger, balances and settlements',
    items: [
      { label: 'Ledger', Icon: BookOpen, path: '/admin/ledger', perm: 'can_view_ledger' },
      { label: 'Treasury', Icon: Landmark, path: '/admin/treasury', perm: 'can_view_treasury' },
      { label: 'Customer Wallets', Icon: Wallet, path: '/admin/customer-wallets', perm: 'can_view_customer_wallets' },
      { label: 'Vendor Payouts', Icon: Banknote, path: '/admin/vendor-payouts', perm: 'can_view_vendor_payouts' },
      { label: 'Approvals', Icon: CircleCheck, path: '/admin/approval-requests', perm: 'can_view_approvals' },
    ],
  },
  {
    title: 'Growth',
    tint: C.amber,
    tintLight: C.amberLight,
    caption: 'Campaigns, sponsors and agents',
    items: [
      { label: 'Promotions', Icon: Gift, path: '/admin/promotions', perm: 'can_view_treasury' },
      { label: 'Sponsors', Icon: Megaphone, path: '/admin/sponsors', perm: 'can_view_treasury' },
      { label: 'Agents', Icon: Handshake, path: '/admin/agents', perm: 'can_view_treasury' },
    ],
  },
  {
    title: 'Access & setup',
    tint: C.cyan,
    tintLight: C.cyanLight,
    caption: 'Accounts, permissions and configuration',
    items: [
      { label: 'Users', Icon: Users, path: '/admin/users', perm: 'can_manage_users' },
      { label: 'Staff', Icon: UserCog, path: '/admin/staff', perm: 'super_admin' },
      { label: 'Roles & Limits', Icon: Medal, path: '/admin/ranks', perm: 'can_view_ranks' },
      { label: 'Server Health', Icon: Activity, path: '/admin/server-health', perm: 'super_admin' },
      { label: 'Settings', Icon: Settings, path: '/admin/settings', perm: 'super_admin' },
    ],
  },
];

// Pulled out so hover can be real state rather than a CSS file. The active row
// carries a green rail on its left edge, which survives being scanned quickly
// far better than a background tint alone.
const NavButton = ({ item, active, tint, tintLight, onClick, tight }) => {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={tight ? item.label : undefined}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: tight ? 'center' : 'flex-start',
        gap: 13,
        width: '100%',
        padding: tight ? '8px 0' : '10px 12px 10px 16px',
        marginBottom: 3,
        textAlign: 'left',
        background: active ? C.surfaceSunk : hover ? C.surfaceSunk : 'transparent',
        border: 'none',
        borderRadius: RADIUS.md,
        color: active ? C.ink : C.inkSoft,
        cursor: 'pointer',
        fontSize: 14.5,
        fontWeight: active ? 700 : 500,
        letterSpacing: '-0.01em',
        transition: 'background 0.12s ease, color 0.12s ease',
      }}
    >
      {active && !tight && (
        <span
          style={{
            position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
            width: 4, height: 22, borderRadius: '0 4px 4px 0', background: tint,
          }}
        />
      )}
      {/* Neutral at rest. Colour is reserved for the page you are actually on,
          so exactly one row in the whole drawer carries it at any moment —
          colour as a signal rather than as decoration. */}
      <span
        style={{
          width: 32, height: 32, borderRadius: RADIUS.sm, flexShrink: 0,
          display: 'grid', placeItems: 'center',
          background: active ? tintLight : C.surfaceSunk,
          transition: 'background 0.12s ease',
        }}
      >
        {/* The stroke takes the section tint only when active, so exactly one
            icon in the drawer is coloured at a time. */}
        <item.Icon size={17} strokeWidth={2} color={active ? tint : C.inkSoft} />
      </span>
      {!tight && (
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.label}
        </span>
      )}
    </button>
  );
};

const AdminNav = ({ collapsed = false, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { isSuperAdmin, can } = useAdminPermissions();
  const { isMobile } = useResponsive();

  const [open, setOpen] = useState(false);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const go = (path) => {
    setOpen(false);
    if (location.pathname !== path) navigate(path);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } finally {
      navigate('/login');
    }
  };

  // A section with nothing visible in it should not leave its heading behind.
  const sections = useMemo(() => {
    const allow = (perm) => {
      if (!perm) return true;
      if (perm === 'super_admin') return isSuperAdmin;
      return isSuperAdmin || can(perm);
    };
    return SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((i) => allow(i.perm)) }))
      .filter((s) => s.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, can]);

  // The brand block, section list and sign-out are identical either side of
  // the mobile/desktop split, so they are built once and given a `tight` flag
  // rather than duplicated.
  const brand = (tight) => (
    <div
      style={{
        padding: tight ? '17px 0' : '17px 16px 17px 70px',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: tight ? 'center' : 'flex-start',
        gap: 10, flexShrink: 0,
      }}
    >
      <img
        src={logo}
        alt="Pharvlink"
        style={{ width: 38, height: 38, objectFit: 'contain', display: 'block', flexShrink: 0 }}
      />
      {!tight && (
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, color: C.ink, fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em' }}>
            Pharv<span style={{ color: C.primary }}>link</span>
          </p>
          <p style={{ margin: '2px 0 0', color: C.inkFaint, fontSize: 11 }}>
            Admin Control Panel
          </p>
        </div>
      )}
    </div>
  );

  // Collapsed, a heading and its caption have nowhere to go. The section's
  // tint becomes a short coloured bar instead, so the grouping and its colour
  // coding both survive the narrow column.
  const sectionList = (tight) => (
    <div className="pv-scroll" style={{ padding: tight ? '14px 10px 20px' : '16px 13px 20px', flex: 1, overflowY: 'auto' }}>
      {sections.map((section, idx) => (
        <div key={section.title} style={{ marginBottom: idx === sections.length - 1 ? 6 : (tight ? 14 : 22) }}>
          {tight ? (
            <div style={{ display: 'grid', placeItems: 'center', marginBottom: 7 }}>
              <span style={{ width: 16, height: 2.5, borderRadius: 2, background: section.tint }} />
            </div>
          ) : (
            <div style={{ padding: '0 12px', marginBottom: 9 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 10, height: 2.5, borderRadius: 2, background: section.tint, flexShrink: 0 }} />
                <p
                  style={{
                    margin: 0, fontSize: 11.5, fontWeight: 800, color: C.inkSoft,
                    textTransform: 'uppercase', letterSpacing: '0.08em',
                  }}
                >
                  {section.title}
                </p>
              </div>
              <p style={{ margin: '4px 0 0', fontSize: 11.5, color: C.inkFaint, lineHeight: 1.4 }}>
                {section.caption}
              </p>
            </div>
          )}

          {section.items.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              tint={section.tint}
              tintLight={section.tintLight}
              active={isActive(item.path)}
              onClick={() => go(item.path)}
              tight={tight}
            />
          ))}
        </div>
      ))}
    </div>
  );

  const signOutBtn = (tight) => (
    <div style={{ padding: 13, borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
      <button
        onClick={handleLogout}
        title={tight ? 'Sign out' : undefined}
        style={{
          width: '100%', padding: tight ? '10px 0' : '12px', borderRadius: RADIUS.md,
          border: `1px solid ${C.border}`, background: C.surface,
          color: C.inkSoft, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        }}
      >
        <LogOut size={16} strokeWidth={2} color={C.inkSoft} />{!tight && <span>Sign out</span>}
      </button>
    </div>
  );

  // ── Desktop: persistent, collapsible sidebar ───────────────────────────
  if (!isMobile) {
    return (
      <nav
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0,
          width: collapsed ? ADMIN_SIDEBAR_COLLAPSED : ADMIN_SIDEBAR_WIDTH,
          background: C.surface,
          borderRight: `1px solid ${C.border}`,
          zIndex: 250,
          display: 'flex', flexDirection: 'column',
          transition: 'width 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        }}
      >
        <ScrollStyle />
        {brand(collapsed)}

        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          style={{
            display: 'flex', alignItems: 'center', gap: 9,
            justifyContent: collapsed ? 'center' : 'flex-start',
            width: 'auto', margin: collapsed ? '10px 10px 0' : '10px 13px 0',
            padding: collapsed ? '7px 0' : '7px 12px',
            background: 'transparent', border: `1px solid ${C.border}`,
            borderRadius: RADIUS.md, cursor: 'pointer',
            color: C.inkSoft, fontSize: 13, lineHeight: 1,
          }}
        >
          {collapsed
            ? <PanelLeftOpen size={16} strokeWidth={2} color={C.inkSoft} />
            : <PanelLeftClose size={16} strokeWidth={2} color={C.inkSoft} />}
          {!collapsed && <span>Collapse</span>}
        </button>

        {sectionList(collapsed)}
        {signOutBtn(collapsed)}
      </nav>
    );
  }

  // ── Mobile: the existing drawer, unchanged ─────────────────────────────
  return (
    <>
      <ScrollStyle />
      {/* Trigger */}
      <button
        onClick={() => setOpen((p) => !p)}
        aria-label={open ? 'Close menu' : 'Open menu'}
        style={{
          position: 'fixed', top: 14, left: 14, zIndex: 300,
          width: 44, height: 44, borderRadius: RADIUS.md,
          background: open ? C.primary : C.surface,
          border: `1px solid ${open ? C.primary : C.borderStrong}`,
          color: open ? '#ffffff' : C.ink,
          boxShadow: open ? SHADOW.pop : SHADOW.card,
          fontSize: 18, cursor: 'pointer', lineHeight: 1,
          transition: 'background 0.15s ease, border-color 0.15s ease',
        }}
      >
        {open
          ? <X size={19} strokeWidth={2.2} style={{ display: 'block', margin: '0 auto' }} />
          : <Menu size={19} strokeWidth={2.2} style={{ display: 'block', margin: '0 auto' }} />}
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.28)', zIndex: 200 }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0, width: 304,
          background: C.surface,
          borderRight: `1px solid ${C.border}`,
          zIndex: 250,
          display: 'flex', flexDirection: 'column',
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: open ? SHADOW.pop : 'none',
        }}
      >
        {brand(false)}
        {sectionList(false)}
        {signOutBtn(false)}
      </div>
    </>
  );
};

export default AdminNav;